import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../../../components/BrandLogo/BrandLogo';
import { NAVY } from '../websiteTokens';
import { useWebsiteTheme } from '../useWebsiteTheme';
import ThemeToggle from '../components/ThemeToggle';

const BRAND = 'EzeitPro';
const COMPANY = 'UtilStop LLC-FZ';
const EFFECTIVE = 'May 11, 2026';
const CONTACT_EMAIL = 'sales@ezeitpro.com';
const WEBSITE = 'www.ezeitpro.com';

const tocItems = [
  { id: 'acceptance', label: '1. Acceptance of Terms' },
  { id: 'services', label: '2. Description of Services' },
  { id: 'account', label: '3. Account Registration & Security' },
  { id: 'subscriptions', label: '4. Subscriptions & Billing' },
  { id: 'acceptable-use', label: '5. Acceptable Use' },
  { id: 'ip', label: '6. Intellectual Property' },
  { id: 'data', label: '7. Data & Privacy' },
  { id: 'third-party', label: '8. Third-Party Services' },
  { id: 'disclaimers', label: '9. Disclaimers & Warranties' },
  { id: 'liability', label: '10. Limitation of Liability' },
  { id: 'indemnification', label: '11. Indemnification' },
  { id: 'termination', label: '12. Termination' },
  { id: 'governing-law', label: '13. Governing Law & Disputes' },
  { id: 'general', label: '14. General Provisions' },
  { id: 'contact', label: '15. Contact Us' },
];

export default function TermsOfService() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useWebsiteTheme();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);


  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const d = isDark ? {
    bg: NAVY.bg, surface: NAVY.surface, bgAlt: NAVY.bgAlt,
    text: NAVY.text, textSec: NAVY.textSec, textMuted: NAVY.textMuted,
    border: NAVY.border,
    primary: '#E91E63', accent: 'rgba(233,30,99,0.18)',
    badgeText: '#F48FB1',
    footBg: NAVY.footBg,
    yellowBg: 'rgba(255,152,0,0.12)', yellowBorder: 'rgba(255,152,0,0.3)',
    sh: '0 1px 4px rgba(3,8,20,0.6)',
  } : {
    bg: '#FAFAFA', surface: '#FFFFFF', bgAlt: '#F5F5F5',
    text: '#212121', textSec: '#424242', textMuted: '#757575',
    border: 'rgba(0,0,0,0.08)',
    primary: '#C2185B', accent: '#FCE4EC',
    badgeText: '#C2185B',
    footBg: '#212121',
    yellowBg: '#FFF8E1', yellowBorder: '#FFE082',
    sh: '0 1px 4px rgba(0,0,0,0.06)',
  };

  // Helper components — defined inside so they close over d and isMobile
  const S = ({ id, title, children }) => (
    <section id={id} style={{ marginBottom: '44px', scrollMarginTop: '80px' }}>
      <h2 style={{ fontSize: isMobile ? '17px' : '19px', fontWeight: 700, color: d.text, borderBottom: `2px solid ${d.accent}`, paddingBottom: '10px', marginBottom: '18px', margin: '0 0 18px' }}>{title}</h2>
      {children}
    </section>
  );

  const P = ({ children }) => (
    <p style={{ fontSize: isMobile ? '14px' : '15px', color: d.textSec, lineHeight: 1.85, marginBottom: '14px', marginTop: 0 }}>{children}</p>
  );

  const Ul = ({ items }) => (
    <ul style={{ margin: '0 0 14px 0', paddingLeft: '22px' }}>
      {items.map((t, i) => (
        <li key={i} style={{ fontSize: isMobile ? '14px' : '15px', color: d.textSec, lineHeight: 1.8, marginBottom: '6px' }}>{t}</li>
      ))}
    </ul>
  );

  const Sub = ({ title }) => (
    <h3 style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: d.text, marginBottom: '8px', marginTop: '18px' }}>{title}</h3>
  );

  const Callout = ({ children }) => (
    <div style={{ background: d.yellowBg, border: `1px solid ${d.yellowBorder}`, borderRadius: '10px', padding: '16px 20px', marginBottom: '16px', fontSize: isMobile ? '13px' : '14px', color: d.textSec, lineHeight: 1.75 }}>
      {children}
    </div>
  );

  const ContactCard = ({ items }) => (
    <div style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {items.map((c, i) => (
        <div key={i} style={{ display: 'flex', gap: '16px', padding: '10px 0', borderBottom: i < items.length - 1 ? `1px solid ${d.border}` : 'none', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: d.textMuted, minWidth: '70px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.label}</span>
          {c.href
            ? <a href={c.href} style={{ fontSize: '14px', color: d.primary, textDecoration: 'none', fontWeight: 500 }}>{c.value}</a>
            : <span style={{ fontSize: '14px', color: d.text }}>{c.value}</span>
          }
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ fontFamily: "'Roboto','Segoe UI',Arial,sans-serif", background: d.bg, color: d.text, minHeight: '100vh', transition: 'background 0.3s,color 0.3s', overflowX: 'hidden' }}>

      {/* Header */}
      <header style={{ background: d.surface, borderBottom: `1px solid ${d.border}`, padding: `0 ${isMobile ? '16px' : '32px'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px', position: 'sticky', top: 0, zIndex: 100, boxShadow: d.sh }}>
        <BrandLogo height={36} tone={isDark ? 'dark' : 'light'} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ThemeToggle isDark={isDark} onToggle={toggleTheme} color={d.text} border={d.border} size={36} />
          <button
            onClick={() => navigate('/')}
            style={{ background: d.bgAlt, border: `1px solid ${d.border}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: d.textSec, fontWeight: 500, whiteSpace: 'nowrap' }}
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Banner */}
      <div style={{ background: isDark ? 'linear-gradient(135deg,#1a0a2e 0%,#16213e 100%)' : `linear-gradient(135deg,${d.accent} 0%,#f5f5f5 100%)`, padding: isMobile ? '36px 20px 30px' : '48px 32px 40px', textAlign: 'center', borderBottom: `1px solid ${d.border}` }}>
        <div style={{ display: 'inline-block', background: d.primary, color: '#fff', fontSize: '11px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Legal Document</div>
        <h1 style={{ fontSize: isMobile ? '26px' : '36px', fontWeight: 700, color: d.text, margin: '0 0 10px' }}>Terms of Use & Service</h1>
        <p style={{ fontSize: '15px', color: d.textMuted, margin: 0 }}>Effective Date: <strong style={{ color: d.textSec }}>{EFFECTIVE}</strong> &nbsp;·&nbsp; {COMPANY}</p>
      </div>

      {/* Body grid */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '28px 16px 60px' : '48px 24px 80px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '220px 1fr', gap: isMobile ? '0' : '48px', alignItems: 'start' }}>

        {/* TOC — desktop sidebar */}
        {!isMobile && (
          <aside style={{ position: 'sticky', top: '80px' }}>
            <div style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '12px', padding: '20px', boxShadow: d.sh }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: d.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '14px' }}>Contents</div>
              {tocItems.map(item => (
                <a key={item.id} href={`#${item.id}`}
                  style={{ display: 'block', fontSize: '13px', color: d.textSec, textDecoration: 'none', padding: '5px 0', borderBottom: `1px solid ${d.border}`, lineHeight: 1.4, transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = d.primary}
                  onMouseLeave={e => e.currentTarget.style.color = d.textSec}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </aside>
        )}

        {/* TOC — mobile pill list */}
        {isMobile && (
          <div style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '12px', padding: '16px', marginBottom: '24px', boxShadow: d.sh }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: d.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>Jump to section</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {tocItems.map(item => (
                <a key={item.id} href={`#${item.id}`}
                  style={{ fontSize: '12px', color: d.primary, background: d.accent, padding: '4px 10px', borderRadius: '20px', textDecoration: 'none', fontWeight: 500 }}
                >
                  {item.label.split('. ')[0]}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Main content */}
        <main>

          <Callout>
            <strong>Please read these Terms carefully.</strong> By creating an account or using {BRAND} in any way, you agree to be bound by these Terms of Use and Service. If you do not agree, you must not use the platform.
          </Callout>

          <S id="acceptance" title="1. Acceptance of Terms">
            <P>These Terms of Use and Service ("Terms") constitute a legally binding agreement between you ("User", "you", or "your") and {COMPANY} ("Company", "we", "our", or "us"), governing your access to and use of the {BRAND} software-as-a-service platform and website at {WEBSITE} (collectively, the "Services").</P>
            <P>By registering for an account, clicking "I Agree," or otherwise accessing or using the Services, you confirm that: (i) you have read and understood these Terms; (ii) you have the legal authority to bind yourself or the entity you represent; and (iii) you agree to be bound by these Terms and our Privacy Policy, which is incorporated herein by reference.</P>
            <P>If you are using the Services on behalf of a business or other legal entity, you represent that you have the authority to bind that entity to these Terms. In that case, "you" and "your" will refer to that entity.</P>
          </S>

          <S id="services" title="2. Description of Services">
            <P>{BRAND} is a cloud-based SaaS platform designed for small and medium service businesses. The Services include, but are not limited to:</P>
            <Ul items={[
              'Multi-channel booking management: web, app, Google Business Profile, and walk-in booking capture',
              'Crew and resource scheduling: staff assignment, availability tracking, and job management',
              'Automated customer communications: SMS and WhatsApp confirmations, reminders, and follow-ups',
              'Invoicing and payment tracking: tax-compliant invoicing for UAE (VAT) and India (GST), Stripe payment integration',
              'Analytics and reporting: revenue trends, staff performance, and customer retention insights',
              'UtilStop Marketplace access: optional demand generation through the open customer marketplace',
              'Subscription plan management: recurring service subscriptions for end customers',
            ]} />
            <P>We reserve the right to modify, add, or discontinue features of the Services at any time, with reasonable notice where such changes materially affect your use of the platform.</P>
          </S>

          <S id="account" title="3. Account Registration & Security">
            <P>To access {BRAND}, you must register for an account. You agree to provide accurate, current, and complete information during registration and to update it promptly if it changes.</P>
            <P>You are solely responsible for:</P>
            <Ul items={[
              'Maintaining the confidentiality of your account credentials',
              'All activity that occurs under your account, whether or not authorised by you',
              'Ensuring that all users accessing the platform through your account comply with these Terms',
              `Immediately notifying us of any unauthorised access or security breach at ${CONTACT_EMAIL}`,
            ]} />
            <P>You may not share your login credentials with third parties, create accounts on behalf of others without authorisation, or use another user's account. We reserve the right to suspend accounts where we suspect unauthorised use or security compromise.</P>
          </S>

          <S id="subscriptions" title="4. Subscriptions & Billing">
            <Sub title="4.1 Subscription Plans" />
            <P>{BRAND} is offered on a subscription basis with the following tiers: Starter (free), Pro, and Enterprise. Features and limits vary by plan as described at {WEBSITE}/pricing. Enterprise plans are subject to separate commercial agreements.</P>
            <Sub title="4.2 Billing and Payment" />
            <Ul items={[
              'Subscription fees are billed in advance on a monthly or annual basis, as selected at the time of purchase',
              'All fees are exclusive of applicable taxes (UAE VAT, India GST) which will be added where required by law',
              'Payment is processed securely through Stripe; by providing payment details you authorise recurring charges',
              'Overage charges (e.g., WhatsApp alerts beyond the included allocation) are billed at the end of each billing period with prior notification',
            ]} />
            <Sub title="4.3 Cancellation and Refunds" />
            <P>You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period; no partial refunds are issued for unused subscription time. Annual subscriptions cancelled within 14 days of initial purchase may be eligible for a full refund at our discretion.</P>
            <Sub title="4.4 Price Changes" />
            <P>We reserve the right to change subscription fees with at least 30 days' written notice. Your continued use after the effective date of a price change constitutes acceptance of the new pricing.</P>
          </S>

          <S id="acceptable-use" title="5. Acceptable Use">
            <P>You agree to use {BRAND} only for lawful business purposes and in compliance with these Terms. You must not:</P>
            <Ul items={[
              'Use the Services to engage in fraudulent, deceptive, or illegal activities',
              'Transmit unsolicited commercial communications (spam) through our messaging integrations',
              'Collect, store, or process sensitive personal data of end customers beyond what is necessary for your legitimate business operations',
              'Attempt to gain unauthorised access to any part of the Services or our infrastructure',
              'Reverse engineer, decompile, or disassemble any part of the platform',
              'Use automated scraping tools, bots, or data extraction methods without our prior written consent',
              'Resell, sublicense, or otherwise commercialise the Services without a separate written agreement',
              'Upload or transmit malware, viruses, or any other harmful code',
              'Interfere with or disrupt the integrity or performance of the Services or other users\' data',
              'Impersonate any person or entity or falsely represent your affiliation with any person or entity',
            ]} />
            <P>Violation of this section may result in immediate suspension or termination of your account, at our sole discretion, without liability.</P>
          </S>

          <S id="ip" title="6. Intellectual Property">
            <Sub title="6.1 Our Intellectual Property" />
            <P>The {BRAND} platform, including all software, code, designs, logos, trademarks, text, graphics, and all other content ("Platform IP"), is the exclusive intellectual property of {COMPANY} or its licensors, protected by UAE, UAE Free Zone, Indian, and international intellectual property laws.</P>
            <P>We grant you a limited, non-exclusive, non-transferable, revocable licence to access and use the Services solely for your internal business purposes during the term of your subscription, subject to these Terms.</P>
            <Sub title="6.2 Your Content" />
            <P>You retain ownership of all data, content, and materials you upload to or create within {BRAND} ("Your Content"). By using the Services, you grant us a limited licence to host, process, and transmit Your Content solely to provide the Services to you. We will not use Your Content for any other purpose.</P>
            <Sub title="6.3 Feedback" />
            <P>If you provide feedback, suggestions, or ideas regarding the Services, you grant us the right to use that feedback without compensation or attribution to you.</P>
          </S>

          <S id="data" title="7. Data & Privacy">
            <P>Your use of the Services involves the collection and processing of personal data as described in our Privacy Policy, available at {WEBSITE}/privacy-policy. The Privacy Policy is incorporated into these Terms by reference.</P>
            <P>Where you use {BRAND} to collect and manage personal data of your own customers, you act as an independent data controller and are responsible for: (i) having a lawful basis for processing that data; (ii) providing your customers with appropriate privacy notices; and (iii) complying with applicable data protection laws in your jurisdiction.</P>
            <P>A Data Processing Agreement (DPA) is available upon request for customers subject to GDPR or UAE PDPL who require formal documentation of our processor role.</P>
          </S>

          <S id="third-party" title="8. Third-Party Services">
            <P>{BRAND} integrates with third-party services including Stripe (payments), WhatsApp Business API (messaging), Google Business Profile, and AWS (infrastructure). These integrations are governed by the terms and privacy policies of the respective providers.</P>
            <P>We are not responsible for the practices, content, or availability of third-party services, and your use of any integrated third-party service is at your own risk and subject to that provider's terms.</P>
          </S>

          <S id="disclaimers" title="9. Disclaimers & Warranties">
            <Callout>
              <strong>Important:</strong> The following disclaimers apply to the maximum extent permitted by applicable law, including UAE law, Indian law, and GDPR.
            </Callout>
            <P>THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR UNINTERRUPTED OR ERROR-FREE OPERATION.</P>
            <P>We do not warrant that the Services will meet all of your requirements, that the Services will be available at all times without interruption, or that errors will be corrected. We strive for 99.9% uptime but cannot guarantee uninterrupted availability.</P>
            <P>Nothing in this section excludes or limits our liability for fraud, death or personal injury caused by our negligence, or any other liability which cannot be lawfully excluded under UAE law or the Consumer Protection Laws applicable in India or the EEA.</P>
          </S>

          <S id="liability" title="10. Limitation of Liability">
            <P>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL {COMPANY.toUpperCase()}, ITS DIRECTORS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR:</P>
            <Ul items={[
              'Loss of profits, revenue, data, business, or goodwill',
              'Indirect, incidental, consequential, special, or punitive damages',
              'Any damages arising from your reliance on the Services for business-critical operations where a backup system was not maintained',
              'Any loss or corruption of data not recoverable through our standard backup procedures',
            ]} />
            <P>OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS ARISING UNDER OR RELATED TO THESE TERMS SHALL NOT EXCEED THE GREATER OF: (A) THE TOTAL FEES PAID BY YOU TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM; OR (B) USD 100.</P>
            <P>These limitations apply regardless of the legal theory (contract, tort, strict liability, or otherwise) and even if we have been advised of the possibility of such damages. Some jurisdictions do not allow certain limitations of liability; in such cases these limitations apply to the maximum extent permissible.</P>
          </S>

          <S id="indemnification" title="11. Indemnification">
            <P>You agree to indemnify, defend, and hold harmless {COMPANY}, its affiliates, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or relating to:</P>
            <Ul items={[
              'Your violation of these Terms',
              'Your use of the Services in a manner not authorised or contemplated by these Terms',
              'Your processing of customer personal data in a way that violates applicable data protection laws',
              'Any content you upload, transmit, or store on the platform that infringes third-party rights',
              'Any dispute between you and your customers',
            ]} />
          </S>

          <S id="termination" title="12. Termination">
            <Sub title="12.1 Termination by You" />
            <P>You may terminate your account at any time by cancelling your subscription through your account settings. Your access will continue until the end of the current billing period.</P>
            <Sub title="12.2 Termination by Us" />
            <P>We may suspend or terminate your account immediately, without prior notice or liability, if you: breach these Terms; fail to pay fees when due; engage in fraudulent or illegal activity; or where we are required to do so by applicable law or a competent authority.</P>
            <Sub title="12.3 Effect of Termination" />
            <P>Upon termination, your licence to use the Services ceases immediately. We will retain your data for 90 days after termination to allow you to request an export. After this period, your data will be deleted in accordance with our Privacy Policy, except where retention is required by law.</P>
          </S>

          <S id="governing-law" title="13. Governing Law & Dispute Resolution">
            <P>These Terms are governed by and construed in accordance with the laws of the United Arab Emirates, specifically the laws of the Dubai International Financial Centre (DIFC), without regard to conflict of law principles.</P>
            <Sub title="For UAE Users" />
            <P>Any dispute arising from these Terms shall first be subject to good-faith negotiation. If not resolved within 30 days, disputes shall be referred to and finally resolved by arbitration under the DIFC-LCIA Arbitration Rules in Dubai, conducted in English.</P>
            <Sub title="For India Users" />
            <P>Disputes with Indian users shall be subject to the exclusive jurisdiction of the courts in Mumbai, India, and governed additionally by the applicable Indian laws. The Arbitration and Conciliation Act, 1996 (as amended) shall apply where arbitration is elected by either party.</P>
            <Sub title="For EEA / UK Users" />
            <P>Nothing in these Terms restricts your rights to bring claims before your local courts under GDPR or applicable consumer protection legislation in your EEA member state or in the United Kingdom.</P>
          </S>

          <S id="general" title="14. General Provisions">
            <Ul items={[
              'Entire Agreement: These Terms, together with the Privacy Policy and any applicable Order Form or DPA, constitute the entire agreement between you and us with respect to the Services.',
              'Severability: If any provision of these Terms is found invalid or unenforceable, the remaining provisions remain in full force and effect.',
              'Waiver: Our failure to enforce any right or provision of these Terms shall not constitute a waiver of that right or provision.',
              `Assignment: You may not assign or transfer your rights or obligations under these Terms without our prior written consent. We may assign our rights to a successor entity in a merger or acquisition.`,
              'Force Majeure: We are not liable for any failure or delay in performance caused by circumstances beyond our reasonable control, including natural disasters, acts of government, or infrastructure failures.',
              `Notices: Legal notices to us must be sent to ${CONTACT_EMAIL}. Notices to you will be sent to the email address associated with your account.`,
              'Language: The governing version of these Terms is the English language version. Translations are provided for convenience only.',
            ]} />
          </S>

          <S id="contact" title="15. Contact Us">
            <P>For questions about these Terms, please contact:</P>
            <ContactCard items={[
              { label: 'Company', value: COMPANY },
              { label: 'Platform', value: BRAND },
              { label: 'Email', value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
              { label: 'Website', value: WEBSITE, href: `https://${WEBSITE}` },
              { label: 'Address', value: 'Dubai, United Arab Emirates' },
            ]} />
          </S>

        </main>
      </div>

      {/* Footer */}
      <footer style={{ background: d.footBg, color: '#9E9E9E', padding: '24px 32px', textAlign: 'center', fontSize: '13px' }}>
        <p style={{ margin: 0 }}>
          © 2026 {COMPANY}, Dubai. All rights reserved. &nbsp;·&nbsp;
          <a href="/privacy-policy" style={{ color: '#9E9E9E', textDecoration: 'none' }}>Privacy</a> &nbsp;·&nbsp;
          <a href="/terms" style={{ color: d.primary, textDecoration: 'none' }}>Terms</a> &nbsp;·&nbsp;
          <a href="/security" style={{ color: '#9E9E9E', textDecoration: 'none' }}>Security</a>
        </p>
      </footer>
    </div>
  );
}
